
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isRTL = i18n.language === 'ar';
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    document.documentElement.lang = newLang;
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  };

  const LogoContent = () => (
    <div className="relative">
      <img
        src="https://static.readdy.ai/image/439cae8384efc9e7b28c054739e211d1/7857bf78d2c9072a724b80513f3fbc5c.png"
        alt="EVE Smart Agricultural Services - إيف سمارت للخدمات الزراعية"
        title="EVE Smart - Smart Agriculture Qatar"
        className={`h-16 w-auto cursor-pointer transition-all duration-300 ${
          isScrolled 
            ? 'drop-shadow-md' 
            : 'drop-shadow-[0_0_15px_rgba(255,255,255,0.8)] brightness-110'
        }`}
        onClick={() => scrollToSection('home')}
      />
    </div>
  );

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Desktop Navigation - Right Side */}
          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('home')}
              className={`font-tajawal text-base transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-900 hover:text-primary' : 'text-white hover:text-primary'
              }`}
            >
              {t('nav.home')}
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className={`font-tajawal text-base transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-900 hover:text-primary' : 'text-white hover:text-primary'
              }`}
            >
              {t('nav.services')}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className={`font-tajawal text-base transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-900 hover:text-primary' : 'text-white hover:text-primary'
              }`}
            >
              {t('nav.about')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className={`font-tajawal text-base transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-gray-900 hover:text-primary' : 'text-white hover:text-primary'
              }`}
            >
              {t('nav.contact')}
            </button>
            <button
              onClick={toggleLanguage}
              className={`font-tajawal text-base px-4 py-2 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                isScrolled
                  ? 'bg-primary text-white hover:bg-primary-dark'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {t('nav.language')}
            </button>
          </div>

          {/* Logo - Center */}
          <div className="absolute left-1/2 transform -translate-x-1/2">
            {isHomePage ? (
              <h1 className="m-0 p-0">
                <LogoContent />
              </h1>
            ) : (
              <div>
                <LogoContent />
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden text-2xl cursor-pointer ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}
          >
            <i className={isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white shadow-lg rounded-lg mt-2 py-4">
            <button
              onClick={() => scrollToSection('home')}
              className={`block w-full text-${isRTL ? 'right' : 'left'} px-6 py-3 font-tajawal text-gray-900 hover:bg-primary/10 cursor-pointer`}
            >
              {t('nav.home')}
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className={`block w-full text-${isRTL ? 'right' : 'left'} px-6 py-3 font-tajawal text-gray-900 hover:bg-primary/10 cursor-pointer`}
            >
              {t('nav.services')}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className={`block w-full text-${isRTL ? 'right' : 'left'} px-6 py-3 font-tajawal text-gray-900 hover:bg-primary/10 cursor-pointer`}
            >
              {t('nav.about')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className={`block w-full text-${isRTL ? 'right' : 'left'} px-6 py-3 font-tajawal text-gray-900 hover:bg-primary/10 cursor-pointer`}
            >
              {t('nav.contact')}
            </button>
            <button
              onClick={toggleLanguage}
              className={`block w-full text-${isRTL ? 'right' : 'left'} px-6 py-3 font-tajawal text-primary hover:bg-primary/10 cursor-pointer`}
            >
              {t('nav.language')}
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
